import React, { ReactElement } from 'react';

// Assets
import emptyModelsImage from '@/images/design-system-v2/empty-state/emptyModels.png';

// Components
import ActionsButton, { buttonAction } from '@/views/components/buttons/ActionsButton';
import ConnectedPrivateComponent from '@/views/components/PrivateComponent';
import { DropdownButtonMenuItem } from '@/views/components/buttons/DropdownButton';
import EmptyStateCard from '@/views/components/misc/EmptyStateCard';
import PortfoliosTableV2 from '@/views/components/tables/PortfoliosTableV2';
import RateLimitedComponent from '@/views/components/RateLimitedComponent';
import { LoadingSpinner } from '@/views/components/common';
import { PortfoliosTable } from '@/views/components/tables';
import { SearchInput } from '@/views/components/inputs';
import { Typography } from '@capintel/component-lib/typography';

// Legacy Component
import LegacyMyModelsTab from '@/views/containers/models/LegacyMyModelsTab';

// Enums
import { PORTFOLIO_METRICS_FIELDS, PORTFOLIO_TABLE_COLUMNS } from '@/enums/Portfolio';

// Hooks
import useThemeVersion from '@/hooks/useThemeVersion';

// Libraries
import classNames from 'classnames';
import { SortingRule } from 'react-table';

// Redux
import { RateLimitType } from '@/redux/modules/types/rate-limits';

// Utils
import { canAccessUploadCSV } from '@/utils/feature-access';
import { formatMessage, FormattedMessage } from '@/utils/i18n';
import { PERMISSIONS, userHasPermission, userHasPermissionEnabled, userHasPermissions } from '@/utils/permissions';

// Types
import User from '@/types/user';
import Portfolio from '@/types/portfolio';
import { ALLOWED_HTML_TAGS } from '@/views/components/i18n/FormattedMessageV2';

type Props = {
  user: User;
  query?: string;
  searching: boolean;
  initialized: boolean;
  models: Portfolio[];
  comparingModelIds: string[];
  hasMore: boolean;
  evaluatingPortfolioForComparison?: boolean;
  sortedColumns?: SortingRule[];
  highlightedIds?: string[];
  onSearch: (query: string) => void;
  onLoadMore: () => void;
  onDeletePortfolio: (portfolios: Portfolio[]) => void;
  onEditPortfolio: (portfolio: Portfolio) => void;
  onClonePortfolio?: (portfolio: Portfolio) => void;
  onCompareSelected: (portfolios: Portfolio[]) => void;
  onCreatePortfolio?: () => void;
  onUploadCSV?: () => void;
};

type LegacyProps = {
  portfoliosTableRef: React.RefObject<PortfoliosTable<Portfolio>>;
  onSort: (sorting: SortingRule[]) => void;
  createDropdownItems: () => DropdownButtonMenuItem[];
};

type WrapperProps = LegacyProps & Props;

class MyModelsTabV2 extends React.Component<WrapperProps> {
  containerRef = React.createRef<HTMLDivElement>();

  private i18nEmptyStatePath = 'EmptyStateCard.Models';

  getDisplayedColumns = () => {
    const { user } = this.props;

    return [
      {
        key: PORTFOLIO_TABLE_COLUMNS.NAME,
      },
      {
        key: PORTFOLIO_TABLE_COLUMNS.LOCK,
      },
      {
        key: PORTFOLIO_TABLE_COLUMNS.RISK_PROFILE,
      },
      {
        key: PORTFOLIO_TABLE_COLUMNS.LAST_MODIFIED,
      },
      {
        key: PORTFOLIO_TABLE_COLUMNS.SHOW_DETAILS,
        hidden: !userHasPermissionEnabled(user, PERMISSIONS.SHOW_PORTFOLIO_DETAILS),
      },
      {
        key: PORTFOLIO_TABLE_COLUMNS.COMPARE,
      },
      {
        key: PORTFOLIO_TABLE_COLUMNS.MENU_TOGGLE,
      },
    ]
      .filter(col => !col.hidden)
      .map(col => col.key);
  };

  renderPortfoliosSection = (title: string, portfolios: Portfolio[]) => {
    const {
      evaluatingPortfolioForComparison,
      comparingModelIds,
      hasMore,
      initialized,
      onCreatePortfolio,
      onCompareSelected,
      onDeletePortfolio,
      onEditPortfolio,
      onClonePortfolio,
      onLoadMore,
      onSearch,
      onUploadCSV,
      query,
      searching,
      user,
      highlightedIds,
    } = this.props;

    const displayedColumns = this.getDisplayedColumns();
    const displayedMetrics = [PORTFOLIO_METRICS_FIELDS.MER, PORTFOLIO_METRICS_FIELDS.YEAR_ONE, PORTFOLIO_METRICS_FIELDS.YEAR_THREE];
    const canCreate = userHasPermissions(user, [PERMISSIONS.USER_MODELS, PERMISSIONS.USER_MODELS_CREATE]);
    const showEmptyCard = portfolios.length === 0 && initialized && !query && !searching;
    const isUploadCSVFeatureEnabled = canAccessUploadCSV(user);

    const getActionsForButton = (): buttonAction[] => [
      {
        condition: true,
        icon: 'add',
        node: <FormattedMessage id="ModelsV2.Buttons.NewModel" />,
        onClick: this.props.onCreatePortfolio,
      },
      {
        condition: isUploadCSVFeatureEnabled,
        icon: 'file-upload',
        node: <FormattedMessage id="Comparison.UploadCSVFile" />,
        onClick: this.props.onUploadCSV,
      },
    ];

    const getEmptyStateCard = (): JSX.Element => {
      const { user } = this.props;
      const canCreate = userHasPermissions(user, [PERMISSIONS.USER_MODELS, PERMISSIONS.USER_MODELS_CREATE]) && onCreatePortfolio;
      const canUpload = isUploadCSVFeatureEnabled && onUploadCSV;

      const primaryButton = canCreate
        ? {
            label: `${this.i18nEmptyStatePath}.PrimaryButtonText`,
            onClick: onCreatePortfolio,
          }
        : undefined;

      const secondaryActions = {
        links: canCreate && canUpload ? [{ label: `${this.i18nEmptyStatePath}.SecondaryLinkText1`, onClick: onUploadCSV }] : undefined,
      };

      return (
        <EmptyStateCard
          imageAlt="My models placeholder image"
          imagePath={emptyModelsImage}
          messagePath={`${this.i18nEmptyStatePath}.Message`}
          primaryButton={primaryButton}
          titlePath={`${this.i18nEmptyStatePath}.Title`}
          {...secondaryActions}
        />
      );
    };

    return (
      <>
        {showEmptyCard ? (
          getEmptyStateCard()
        ) : (
          <>
            <div className="models-content-header">
              <Typography as={ALLOWED_HTML_TAGS.H2} variant="headerSm">
                {title}
              </Typography>
              <div className="models-content-header-actions">
                <SearchInput
                  className="search-input-v2"
                  loading={searching}
                  key="models"
                  placeholder={formatMessage('Models.SearchMyModels')}
                  onChange={onSearch}
                />
                <ConnectedPrivateComponent forcedFalse={!canCreate}>
                  {() => (
                    <RateLimitedComponent type={RateLimitType.MODELS}>
                      {() => <ActionsButton actions={getActionsForButton()} disabled={evaluatingPortfolioForComparison} />}
                    </RateLimitedComponent>
                  )}
                </ConnectedPrivateComponent>
              </div>
            </div>
            <div className="models-content-portfolios">
              {(searching || !initialized) && (
                <div className="models-content-loading-container">
                  <LoadingSpinner />
                </div>
              )}
              <PortfoliosTableV2<Portfolio>
                className={classNames({ 'visually-hidden': searching || !initialized })}
                comparingPortfolioIds={comparingModelIds}
                displayedColumns={displayedColumns}
                displayedMetrics={displayedMetrics}
                hasMore={hasMore}
                onCompare={onCompareSelected}
                onDelete={onDeletePortfolio}
                onEdit={onEditPortfolio}
                {...(userHasPermission(user, PERMISSIONS.USER_MODELS_CREATE) ? { onCopy: onClonePortfolio } : {})}
                onLoadMore={onLoadMore}
                portfolios={portfolios}
                selectedTitle="PortfoliosTable.SelectedTitle.Models"
                query={query}
                messageIds={{
                  deleteMultiple: 'PortfoliosTable.Buttons.DeleteSelectedModels',
                  deleteSingle: 'PortfoliosTable.Buttons.DeleteModel',
                  editSingle: 'PortfoliosTable.Buttons.EditModel',
                  copySingle: 'PortfoliosTable.Buttons.CopyModel',
                }}
                canEditPredicate={portfolio => !portfolio.availableActions || portfolio.availableActions.edit}
                canClonePredicate={portfolio => !portfolio.availableActions || portfolio.availableActions.clone}
                canDeletePredicate={portfolio => !portfolio.availableActions || portfolio.availableActions.delete}
                canAcceptPredicate={() => false}
                serverSideSorting={!userHasPermission(user, PERMISSIONS.USER_MODELS_CLIENT_SIDE_SORTING)}
                onServerSideSorting={sort => this.props.onSort([sort])}
                sortedColumns={this.props.sortedColumns}
                highlightedIds={highlightedIds}
              />
            </div>
          </>
        )}
      </>
    );
  };

  render() {
    const { models } = this.props;

    return (
      <div ref={this.containerRef} className="models-content">
        <ConnectedPrivateComponent permissions={[PERMISSIONS.USER_MODELS]}>
          {() => <>{this.renderPortfoliosSection(formatMessage('Models.MyModels'), models)}</>}
        </ConnectedPrivateComponent>
      </div>
    );
  }
}

const MyModelsTab = (props: WrapperProps): ReactElement => {
  const { isLightVersion } = useThemeVersion();
  if (isLightVersion) {
    return <MyModelsTabV2 {...props} />;
  }
  return <LegacyMyModelsTab {...props} />;
};

export default MyModelsTab;
